'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/shared/components/ui';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import { FeatherChevronDown, FeatherChevronUp, FeatherPhone } from '@subframe/core';
import type { NegociacaoCasoTelefoneUi } from '../../../types/negociacaoDetailTypes';
import { encontrarNumeroAtivo, listarCanaisDoChat, resolveTagDoCanalAtivo, resolveTelefoneTag } from '../../../utils/resolveTelefoneTag';
import { TelefoneTagBadge } from '../../common/TelefoneTagBadge';
export interface CanalAtivoBarProps {
  telefones: NegociacaoCasoTelefoneUi[];
  /** A conversa que a aba está mostrando — pode não ser a do número ativo. */
  canalVisualizadoId: string | null;
  /** Telefone do caso, para negociação legada sem fila. */
  counterpartyPhone: string;
  onSelecionarCanal: (conversaId: string | null) => void;
}

/**
 * A barra de canal acima da aba Chat.
 *
 * Diz de qual número é a conversa exibida e abre a lista dos outros contatos do caso. É um card
 * próprio (borda arredondada, fora do card do chat), como no design — antes ela era uma faixa
 * dentro do chat e se lia como cabeçalho da conversa, não como seletor.
 *
 * A barra INTEIRA é o gatilho do dropdown: o alvo de clique é a linha toda, não só o texto da
 * direita. O rótulo diz `Canal ativo` só quando a aba mostra o número que negocia agora; com outro
 * número selecionado vira `Canal visualizado` — "ativo" mentiria sobre um canal que não está mais
 * em uso.
 */
export function CanalAtivoBar(t0) {
  const $ = _c(50);
  const {
    telefones,
    canalVisualizadoId,
    counterpartyPhone,
    onSelecionarCanal
  } = t0;
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== aberto) {
    t1 = () => {
      if (!aberto) {
        return;
      }
      const aoClicarFora = evento => {
        if (!containerRef.current?.contains(evento.target as Node)) {
          setAberto(false);
        }
      };
      document.addEventListener("mousedown", aoClicarFora);
      return () => document.removeEventListener("mousedown", aoClicarFora);
    };
    t2 = [aberto];
    $[0] = aberto;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let ativo;
  let mostrandoOAtivo;
  let numero;
  let t3;
  let t4;
  let visualizado;
  if ($[3] !== canalVisualizadoId || $[4] !== counterpartyPhone || $[5] !== telefones) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      ativo = encontrarNumeroAtivo(telefones);
      if (telefones.length <= 1) {
        t4 = null;
        break bb0;
      }
      visualizado = telefones.find(telefone => telefone.conversaId === canalVisualizadoId) ?? ativo;
      mostrandoOAtivo = visualizado?.id === ativo?.id;
      numero = visualizado?.numeroNormalizado ?? counterpartyPhone;
      t3 = mostrandoOAtivo ? resolveTagDoCanalAtivo(telefones) : visualizado ? resolveTelefoneTag(visualizado) : null;
    }
    $[3] = canalVisualizadoId;
    $[4] = counterpartyPhone;
    $[5] = telefones;
    $[6] = ativo;
    $[7] = mostrandoOAtivo;
    $[8] = numero;
    $[9] = t3;
    $[10] = t4;
    $[11] = visualizado;
  } else {
    ativo = $[6];
    mostrandoOAtivo = $[7];
    numero = $[8];
    t3 = $[9];
    t4 = $[10];
    visualizado = $[11];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  const tag = t3;
  let t5;
  if ($[12] !== aberto || $[13] !== ativo || $[14] !== mostrandoOAtivo || $[15] !== numero || $[16] !== onSelecionarCanal || $[17] !== tag || $[18] !== telefones || $[19] !== visualizado?.id) {
    let t6;
    if ($[21] !== ativo?.id || $[22] !== visualizado?.id) {
      t6 = telefone_0 => telefone_0.id !== visualizado?.id && telefone_0.id !== ativo?.id;
      $[21] = ativo?.id;
      $[22] = visualizado?.id;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const outros = listarCanaisDoChat(telefones).filter(t6).sort(_temp);
    const mostraVoltarAoAtivo = !mostrandoOAtivo && ativo !== null;
    const totalNoDropdown = outros.length + (mostraVoltarAoAtivo ? 1 : 0);
    const Chevron = aberto ? FeatherChevronUp : FeatherChevronDown;
    let t7;
    if ($[24] !== totalNoDropdown) {
      t7 = () => totalNoDropdown > 0 && setAberto(_temp2);
      $[24] = totalNoDropdown;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    const t8 = totalNoDropdown > 0 ? aberto : undefined;
    const t9 = totalNoDropdown > 0 ? "listbox" : undefined;
    let t10;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <span className="border-brand-100 text-brand-700 flex size-8 shrink-0 items-center justify-center rounded-[8px] border bg-white"><FeatherPhone className="size-4" aria-hidden="true" /></span>;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const t11 = mostrandoOAtivo ? "Canal ativo" : "Canal visualizado";
    let t12;
    if ($[27] !== t11) {
      t12 = <span className="text-brand-700 text-[10px] leading-none font-semibold tracking-wide uppercase">{t11}</span>;
      $[27] = t11;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== numero) {
      t13 = formatTelefoneComCodigoPais(numero);
      $[29] = numero;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== t13) {
      t14 = <span className="text-body-xs-semibold text-default-font break-all">{t13}</span>;
      $[31] = t13;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== t12 || $[34] !== t14) {
      t15 = <span className="flex min-w-0 flex-col gap-0.5">{t12}{t14}</span>;
      $[33] = t12;
      $[34] = t14;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== tag) {
      t16 = tag ? <TelefoneTagBadge tag={tag} /> : null;
      $[36] = tag;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== t15 || $[39] !== t16) {
      t17 = <span className="flex min-w-0 items-center gap-2.5">{t10}<span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">{t15}{t16}</span></span>;
      $[38] = t15;
      $[39] = t16;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    let t18;
    if ($[41] !== Chevron || $[42] !== totalNoDropdown) {
      t18 = totalNoDropdown > 0 && <span className="border-brand-100 text-brand-700 flex shrink-0 items-center gap-2 rounded-[8px] border bg-white px-2.5 py-1.5 text-xs font-medium">Ver outros canais<span className="bg-brand-100 flex size-5 items-center justify-center rounded-full text-[10px] font-semibold">{totalNoDropdown}</span><Chevron className="size-3.5" aria-hidden="true" /></span>;
      $[41] = Chevron;
      $[42] = totalNoDropdown;
      $[43] = t18;
    } else {
      t18 = $[43];
    }
    let t19;
    if ($[44] !== t17 || $[45] !== t18 || $[46] !== t7 || $[47] !== t8 || $[48] !== t9) {
      t19 = <button type="button" onClick={t7} aria-expanded={t8} aria-haspopup={t9} className="border-brand-100 bg-brand-50/30 hover:bg-brand-50/60 focus-visible:outline-brand-600 flex w-full flex-wrap items-center justify-between gap-2 rounded-[12px] border px-3 py-2.5 text-left transition-colors focus-visible:outline-2">{t17}{t18}</button>;
      $[44] = t17;
      $[45] = t18;
      $[46] = t7;
      $[47] = t8;
      $[48] = t9;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    t5 = <div ref={containerRef} className="relative w-full">{t19}{aberto && totalNoDropdown > 0 && <div role="listbox" className="absolute top-full right-0 left-0 z-20 mt-1 flex flex-col gap-1 rounded-[12px] border border-solid border-neutral-200 bg-white p-2 shadow-md">{mostraVoltarAoAtivo && ativo && <LinhaDoCanal numero={ativo.numeroNormalizado} badge={{
          label: "Negociando",
          variant: "brand"
        }} onSelecionar={() => {
          onSelecionarCanal(ativo.conversaId ?? null);
          setAberto(false);
        }} />}{outros.map(telefone_1 => {
          const tagDaLinha = resolveTelefoneTag(telefone_1);
          const conversaId = telefone_1.conversaId ?? null;
          return <LinhaDoCanal key={telefone_1.id} numero={telefone_1.numeroNormalizado} badge={tagDaLinha} onSelecionar={conversaId ? () => {
            onSelecionarCanal(conversaId);
            setAberto(false);
          } : null} />;
        })}</div>}</div>;
    $[12] = aberto;
    $[13] = ativo;
    $[14] = mostrandoOAtivo;
    $[15] = numero;
    $[16] = onSelecionarCanal;
    $[17] = tag;
    $[18] = telefones;
    $[19] = visualizado?.id;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}
function _temp2(atual) {
  return !atual;
}
function _temp(a, b) {
  return a.ordem - b.ordem;
}
function LinhaDoCanal(t0) {
  const $ = _c(15);
  const {
    numero,
    badge,
    onSelecionar
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex size-7 shrink-0 items-center justify-center rounded-[8px] bg-neutral-100 text-neutral-500"><FeatherPhone className="size-3.5" aria-hidden="true" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== numero) {
    t2 = formatTelefoneComCodigoPais(numero);
    $[1] = numero;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="flex min-w-0 items-center gap-2.5">{t1}<span className="text-body-xs-medium text-default-font break-all">{t2}</span></span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== badge) {
    t4 = badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null;
    $[5] = badge;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const conteudo = t5;
  if (!onSelecionar) {
    let t6;
    if ($[10] !== conteudo) {
      t6 = <div role="option" aria-selected={false} aria-disabled={true} className="flex min-h-11 flex-wrap items-center justify-between gap-2 rounded-[8px] bg-neutral-50 px-3 py-2">{conteudo}</div>;
      $[10] = conteudo;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t6;
  if ($[12] !== conteudo || $[13] !== onSelecionar) {
    t6 = <button type="button" role="option" aria-selected={false} onClick={onSelecionar} className="hover:bg-brand-50 focus-visible:outline-brand-600 flex min-h-11 w-full flex-wrap items-center justify-between gap-2 rounded-[8px] px-3 py-2 text-left transition-colors focus-visible:outline-2">{conteudo}</button>;
    $[12] = conteudo;
    $[13] = onSelecionar;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}